import { useState } from 'react';
import { X, Download } from 'lucide-react';

interface ExportCSVModalProps {
  batchId: string;
  batchName: string;
  totalContacts: number;
  cleanCount: number;
  onClose: () => void;
}

export function ExportCSVModal({
  batchId,
  batchName,
  totalContacts,
  cleanCount,
  onClose,
}: ExportCSVModalProps) {
  const [exportType, setExportType] = useState<'all' | 'clean' | 'bad'>(cleanCount > 0 ? 'clean' : 'all');
  const badCount = totalContacts - cleanCount;
  const [filename, setFilename] = useState(batchName);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      setError(null);

      // Build download URL with query parameters
      const params = new URLSearchParams({
        filter: exportType,
        filename: filename,
      });

      const url = `${import.meta.env.VITE_API_URL}/batches/${batchId}/download?${params.toString()}`;

      // Fetch the CSV
      const response = await fetch(url);

      if (!response.ok) {
        const errorData = await response.json() as { error?: string };
        throw new Error(errorData.error || 'Export failed');
      }

      // Create blob and trigger download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);

      // Close modal on success
      onClose();
    } catch (err) {
      console.error('Export error:', err);
      setError(err instanceof Error ? err.message : 'Failed to export CSV');
    } finally {
      setIsExporting(false);
    }
  };

  const previewCount = exportType === 'clean' ? cleanCount : exportType === 'bad' ? badCount : totalContacts;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">Export CSV</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isExporting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Export Type
            </label>
            <div className="space-y-2">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="exportType"
                  value="clean"
                  checked={exportType === 'clean'}
                  onChange={(e) => setExportType(e.target.value as 'clean')}
                  disabled={cleanCount === 0 || isExporting}
                  className="mt-1"
                />
                <div className={cleanCount === 0 ? 'opacity-50' : ''}>
                  <div className="font-medium">Clean Contacts Only</div>
                  <div className="text-sm text-gray-500">
                    Export only contacts classified as clean (no Classification column)
                  </div>
                </div>
              </label>

              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="exportType"
                  value="all"
                  checked={exportType === 'all'}
                  onChange={(e) => setExportType(e.target.value as 'all')}
                  disabled={isExporting}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium">All Contacts</div>
                  <div className="text-sm text-gray-500">
                    Export all contacts with Classification column
                  </div>
                </div>
              </label>

              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="exportType"
                  value="bad"
                  checked={exportType === 'bad'}
                  onChange={(e) => setExportType(e.target.value as 'bad')}
                  disabled={badCount === 0 || isExporting}
                  className="mt-1"
                />
                <div className={badCount === 0 ? 'opacity-50' : ''}>
                  <div className="font-medium">Bad Contacts Only</div>
                  <div className="text-sm text-gray-500">
                    Export only landlines, TCPA risks, and other flagged contacts (with Classification column)
                  </div>
                </div>
              </label>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
            <p className="text-sm">
              This will export <strong>{previewCount.toLocaleString()}</strong> contact{previewCount !== 1 ? 's' : ''}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filename
            </label>
            <input
              type="text"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              disabled={isExporting}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter filename"
            />
            <p className="text-xs text-gray-500 mt-1">
              .csv extension will be added automatically
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            disabled={isExporting}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting || !filename.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            {isExporting ? 'Exporting...' : 'Export'}
          </button>
        </div>
      </div>
    </div>
  );
}
